import React, { useContext, useEffect, useReducer } from 'react';
import { changeCategoryPositionReducer } from './changeCategoryPositionReducer';
import { ChangeCategoryPositionActionTypes } from './changeCategoryPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { ICategoryPositionItem, IGetCategoryPositionList } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import {
	ChangeCategoryPositionContext,
	changeCategoryPositionInitialState,
	TChangeCategoryPositionSelectedRestaurant,
	TChangeCategoryPositionSelectedItem,
	TTypeOfChangeCategoryPositionModalWindow,
} from './changeCategoryPositionContext';
import { getCategoryPositionList } from 'backend/categories';

const ChangeCategoryPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeCategoryPositionReducer, changeCategoryPositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetCategoryPositionList, string>(
		state.selectedRestaurant ? getCategoryPositionList : null,
		state.selectedRestaurant?.id || '',
	);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data.categoryPositions,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.selectedRestaurant]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setIDCustomer = (value: TChangeCategoryPositionSelectedItem) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setRestaurant = (value: TChangeCategoryPositionSelectedRestaurant | null) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_RESTAURANT,
			payload: {
				selectedRestaurant: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeCategoryPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: ICategoryPositionItem[]) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: ICategoryPositionItem[]) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeCategoryPositionModalWindow) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
				selectSearchString: value ? '' : state.selectSearchString,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: TChangeCategoryPositionSelectedRestaurant[]) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				selectSearchString: value,
			},
		});
	};

	return (
		<ChangeCategoryPositionContext.Provider
			value={{
				setIDCustomer,
				setRestaurant,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				selectedRestaurant: state.selectedRestaurant,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</ChangeCategoryPositionContext.Provider>
	);
};

export default ChangeCategoryPositionState;
